// The global avatar element catalogue — the avatars.json blob served by the
// backend's getAvatars endpoint and written by setAvatars. NOT part of a
// branch's RemoteConstants: avatars are global, and have their own
// Save (dev) and Publish to Live, outside the branch export.
//
// Element ids are stable integers, allocated per type from `nextIds` and NEVER
// reused: deleting an element leaves a permanent hole, and `nextIds` only ever
// grows. Player avatars reference elements by (type, id), so a reused id would
// silently swap art under existing avatars.
//
// Format 2 (2026-10-08): full-colour art, no tint, so no palettes; every element
// carries its own fit (scale + offset) on the shared square canvas.

// Draw order, back to front: face, eyes, facialHair, hair, hat.
export const AVATAR_ELEMENT_TYPES = ['face', 'eyes', 'facialHair', 'hair', 'hat'] as const;
export type AvatarElementType = typeof AVATAR_ELEMENT_TYPES[number];

// Slots a player avatar may omit entirely.
export const OPTIONAL_AVATAR_TYPES: readonly AvatarElementType[] = ['facialHair', 'hat'];

export const AVATAR_TYPE_LABELS: Record<AvatarElementType, string> = {
    face: 'Face',
    eyes: 'Eyes',
    facialHair: 'Facial Hair',
    hair: 'Hair',
    hat: 'Hat',
};

export const CATALOGUE_FORMAT = 2;

export const AVATAR_SCALE_MIN = 0.1;
export const AVATAR_SCALE_MAX = 3;
export const AVATAR_OFFSET_LIMIT = 1;

export interface AvatarElement {
    id: number;
    fileName: string;
    srcWidth?: number;
    srcHeight?: number;
    // Shown in the editor only; players never see it.
    name?: string;
    // The element's fit on the shared square canvas — see layerBox.
    scale: number;
    offsetX: number;
    offsetY: number;
    // Left out of the player's choices and the randomizer, but avatars already
    // using it keep drawing it.
    hidden?: boolean;
}

// Array order within a type is the order players are offered, and the FIRST
// element is what an avatar falls back to when its own element was deleted.
export interface AvatarCatalogue {
    version: number;
    format: number;
    elements: Record<AvatarElementType, AvatarElement[]>;
    nextIds: Record<AvatarElementType, number>;
}

// The backend's no-blob fallback shape (version 0, ids start at 1). For
// display only — never save this over a catalogue that failed to load.
export function emptyAvatarCatalogue(): AvatarCatalogue {
    return {
        version: 0,
        format: CATALOGUE_FORMAT,
        elements: {face: [], eyes: [], facialHair: [], hair: [], hat: []},
        nextIds: {face: 1, eyes: 1, facialHair: 1, hair: 1, hat: 1},
    };
}

// Brings any stored catalogue to format 2: a format-1 blob loses its palettes
// and its elements get the identity fit. Missing types are filled in, so the
// rest of the editor can assume the full shape.
export function normalizeCatalogue(raw: any): AvatarCatalogue {
    const empty = emptyAvatarCatalogue();
    const elements = {...empty.elements};
    const nextIds = {...empty.nextIds};
    for (const type of AVATAR_ELEMENT_TYPES) {
        elements[type] = (raw?.elements?.[type] ?? []).map((e: any): AvatarElement => ({
            id: e.id,
            fileName: e.fileName,
            srcWidth: e.srcWidth,
            srcHeight: e.srcHeight,
            name: e.name || undefined,
            scale: typeof e.scale === 'number' && e.scale > 0 ? e.scale : 1,
            offsetX: typeof e.offsetX === 'number' ? e.offsetX : 0,
            offsetY: typeof e.offsetY === 'number' ? e.offsetY : 0,
            hidden: e.hidden ? true : undefined,
        }));
        const highest = elements[type].reduce((max, e) => Math.max(max, e.id), 0);
        nextIds[type] = Math.max(raw?.nextIds?.[type] ?? 1, highest + 1);
    }
    return {version: raw?.version ?? 0, format: CATALOGUE_FORMAT, elements, nextIds};
}

// Where one element draws on a square avatar canvas `size` wide, in CSS pixels
// from the canvas's top-left. THE FIT RULE, mirrored by the client's AvatarView
// (as RectTransform anchors), so the two must change together: the art is fitted
// whole ("contain") into a square of side scale × size, centred on the canvas
// centre moved by (offsetX, offsetY) × size — x to the right, y UP.
export function layerBox(element: AvatarElement, size: number) {
    const side = element.scale * size;
    return {
        left: (0.5 + element.offsetX - element.scale / 2) * size,
        top: (0.5 - element.offsetY - element.scale / 2) * size,
        width: side,
        height: side,
    };
}

export function elementLabel(element: AvatarElement): string {
    return element.name || `#${element.id}`;
}

// The element a player avatar naming `id` in this type actually draws: that
// element, else the type's first (the deleted-element fallback), else none — in
// which case the client shows the whole avatar as the placeholder.
export function resolveElement(elements: AvatarElement[], id: number): AvatarElement | undefined {
    return elements.find(e => e.id === id) ?? elements[0];
}

// A test avatar: one element id per type, absent = the type left out.
export type AvatarPicks = Partial<Record<AvatarElementType, number>>;
